import type { RegisteredTrigger, TriggerContext, TriggerRegistry } from './types'

/**
 * Фабрика триггеров
 * Позволяет регистрировать и использовать триггеры
 */
export class TriggerFactory {
  private registry: TriggerRegistry = new Map()

  /**
   * Зарегистрировать триггер
   */
  registerTrigger(trigger: RegisteredTrigger): void {
    this.registry.set(trigger.name, trigger)
  }

  /**
   * Зарегистрировать несколько триггеров
   */
  registerTriggers(triggers: RegisteredTrigger[]): void {
    triggers.forEach(trigger => this.registerTrigger(trigger))
  }

  /**
   * Получить триггер по имени
   */
  getTrigger(name: string): RegisteredTrigger | undefined {
    return this.registry.get(name)
  }

  /**
   * Получить все зарегистрированные триггеры
   */
  getAllTriggers(): RegisteredTrigger[] {
    return Array.from(this.registry.values())
  }

  /**
   * Создать builder с триггерами для конкретного контекста
   * Возвращает объект с методами-триггерами
   * 
   * ВАЖНО: Для TypeScript типизации используйте declaration merging:
   * 
   * declare module '@/core/store/notification-queue' {
   *   interface TriggerBuilder {
   *     myCustomTrigger: (arg: string) => boolean
   *   }
   * }
   */
  createBuilder(context: TriggerContext, trackCallback?: (trigger: any) => void): Record<string, any> {
    const builder: Record<string, any> = {}

    // Создаем методы для каждого зарегистрированного триггера
    this.registry.forEach((trigger, name) => {
      builder[name] = (...args: any[]) => {
        return trigger.fn({ context, trackTrigger: trackCallback }, ...args)
      }
    })

    return builder
  }

  /**
   * Проверить, зарегистрирован ли триггер
   */
  hasTrigger(name: string): boolean {
    return this.registry.has(name)
  }

  /**
   * Удалить триггер
   */
  unregisterTrigger(name: string): boolean {
    return this.registry.delete(name)
  }

  /**
   * Очистить все триггеры
   */
  clear(): void {
    this.registry.clear()
  }

  /**
   * Получить количество зарегистрированных триггеров
   */
  get size(): number {
    return this.registry.size
  }
}
